import { trackingRenderGeometryKey, type IRideRoute } from "@ryde/shared";

/**
 * Stable React key for a route layer. Geometry stays out of it so adding a station does not remount
 * every Marker (that remount is what crashed the ride card while the camera was still NaN).
 * `isTrackingRoute` stays in the key so the planned overlay and the live GPS overlay of the same ride
 * are two instances. Empty id fallback is `"_false"` / `"_true"` — two routes with no ride/stationRoute/course
 * id in the same list would collide; real rides always have one of those ids.
 */
export const routeIdentityKey = (route: IRideRoute): string =>
  `${route.rideId ?? route.stationRouteId ?? route.courseId ?? ""}_${route.isTrackingRoute ?? false}`;

/**
 * Identity of a route's drawn geometry/position. For tracking routes the car position is
 * the last location and the trail grows over time, so this must change as the car moves —
 * otherwise the memo comparator would swallow live position updates and freeze the car
 * marker until the route is remounted (reselect) or the page is refreshed.
 */
export const routeGeometryKey = (route: IRideRoute): string => {
  if (route.trackingRenderState) {
    return trackingRenderGeometryKey(route.trackingRenderState);
  }

  const locations = route.locations;
  if (!locations?.length) {
    return "";
  }

  // Full visit order — swapping middle waypoints must invalidate memo (length + last point alone would not).
  return locations
    .map(
      location =>
        `${location.latitude ?? ""},${location.longitude ?? ""},${location.stationId ?? ""},${location.waypointCategory ?? ""}`,
    )
    .join("|");
};
